import { useEffect, useState } from "react";
import api from "@/lib/api";
import { SavedViews } from "@/components/SavedViews";
import { CheckCircle2, Clock, AlertTriangle, X, ChevronLeft, ChevronRight, MailX } from "lucide-react";

const GROUPS = [
  { k: "", l: "All" },
  { k: "delivered", l: "Delivered" },
  { k: "in_transit", l: "In transit" },
  { k: "bounced", l: "Bounced" },
  { k: "failed", l: "Failed" },
];

export default function Delivery() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const limit = 25;

  useEffect(() => {
    api.get(`/admin/delivery?status=${status}&q=${encodeURIComponent(q)}&skip=${skip}&limit=${limit}`)
      .then(({ data }) => setData(data)).catch(() => {});
  }, [status, q, skip]);
  useEffect(() => { setSkip(0); }, [status, q]);

  const c = data?.counts;
  return (
    <div className="px-4 md:px-8 py-8 max-w-6xl" data-testid="hq-delivery-page">
      <div className="text-xs tracking-widest text-volt">ADMIN HQ · OPERATIONS</div>
      <h1 className="heading text-4xl mt-1">DELIVERY</h1>
      <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
        Every email the platform hands to the provider, with its live delivery result (last 90 days).
      </p>

      {data?.email_quota?.alert && (
        <div data-testid="delivery-quota-banner"
          className={`mt-5 border p-3 flex items-center gap-3 text-xs ${data.email_quota.exhausted ? "border-red-700 bg-red-950/40 text-red-300" : "border-amber-700 bg-amber-950/30 text-amber-300"}`}>
          <AlertTriangle size={14} className="shrink-0" />
          <span>
            {data.email_quota.exhausted ? "Daily email quota reached — sending hard-stopped. " : "Email quota warning. "}
            {data.email_quota.sent_today}/{data.email_quota.quota} sent today ({data.email_quota.pct}%).
          </span>
        </div>
      )}

      {c && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="delivery-summary">
          <SummaryCard icon={CheckCircle2} label="Delivered" value={c.delivered} good />
          <SummaryCard icon={Clock} label="In transit" value={c.in_transit} />
          <SummaryCard icon={AlertTriangle} label="Bounced" value={c.bounced} bad={c.bounced > 0} />
          <SummaryCard icon={AlertTriangle} label="Failed" value={c.failed} bad={c.failed > 0} />
          <SummaryCard icon={MailX} label="Unsubscribed" value={c.unsubscribed} />
          <SummaryCard icon={MailX} label="Suppressed" value={c.suppressed} />
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {GROUPS.map((g) => (
          <button key={g.k} data-testid={`delivery-filter-${g.k || "all"}`} onClick={() => setStatus(g.k)}
            className={`px-3 py-1.5 text-[10px] tracking-widest border ${status === g.k ? "border-blaze text-blaze" : "border-neutral-700 text-neutral-500 hover:border-neutral-500"}`}>
            {g.l.toUpperCase()}
          </button>
        ))}
        <input data-testid="delivery-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search recipient or subject…"
          className="ml-auto bg-black border border-neutral-800 px-3 py-2 text-sm w-64 focus:border-volt outline-none" />
        <SavedViews page="delivery" params={{ status, q }} onApply={(p) => { setStatus(p.status || ""); setQ(p.q || ""); }} />
      </div>

      {!data ? (
        <div className="mt-6 text-neutral-500 text-sm">Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="mt-6 border border-dashed border-neutral-800 p-10 text-neutral-500" data-testid="delivery-empty">No sends match.</div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto border border-neutral-800">
            <table className="w-full text-sm" data-testid="delivery-table">
              <thead>
                <tr className="text-left text-[10px] tracking-widest text-neutral-500 border-b border-neutral-800 bg-appcard">
                  <th className="py-2.5 px-3">STATUS</th>
                  <th className="py-2.5 px-3">TO</th>
                  <th className="py-2.5 px-3">SUBJECT</th>
                  <th className="py-2.5 px-3">SENT</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((e, i) => (
                  <tr key={e.id || i} data-testid={`delivery-row-${i}`} onClick={() => setSelected(e)}
                    className="border-b border-neutral-900 hover:bg-neutral-900/50 cursor-pointer">
                    <td className="py-2 px-3"><GroupBadge g={e.group} status={e.delivery_status} /></td>
                    <td className="py-2 px-3 text-neutral-200 text-xs whitespace-nowrap">{e.to}</td>
                    <td className="py-2 px-3 text-neutral-400 text-xs max-w-[320px] truncate">{e.subject}</td>
                    <td className="py-2 px-3 text-neutral-500 text-xs whitespace-nowrap">{e.sent_at ? new Date(e.sent_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-neutral-500">
            <button data-testid="delivery-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronLeft size={12} /></button>
            <span>{skip + 1}–{Math.min(skip + limit, data.total)} of {data.total}</span>
            <button data-testid="delivery-next" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="border border-neutral-700 px-2.5 py-1.5 disabled:opacity-40"><ChevronRight size={12} /></button>
          </div>
        </>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="delivery-detail-drawer">
          <div className="flex-1 bg-black/60" onClick={() => setSelected(null)} />
          <div className="w-full max-w-md bg-[#0B0B0E] border-l border-neutral-800 h-full overflow-y-auto p-5">
            <div className="flex items-start justify-between">
              <div className="heading text-lg">SEND DETAILS</div>
              <button data-testid="delivery-drawer-close" onClick={() => setSelected(null)} className="text-neutral-500 hover:text-white p-1"><X size={18} /></button>
            </div>
            <div className="mt-4 space-y-2.5 text-sm">
              <Detail label="Status"><GroupBadge g={selected.group} status={selected.delivery_status} /></Detail>
              <Detail label="To">{selected.to}</Detail>
              <Detail label="From">{selected.from}</Detail>
              {selected.reply_to && <Detail label="Reply-to">{selected.reply_to}</Detail>}
              <Detail label="Subject">{selected.subject}</Detail>
              <Detail label="Sent">{selected.sent_at ? new Date(selected.sent_at).toLocaleString() : "—"}</Detail>
              {selected.error && <Detail label="Error"><span className="text-red-400 text-xs">{String(selected.error)}</span></Detail>}
              <Detail label="Provider ID"><span className="font-mono text-[11px] text-neutral-400" data-testid="delivery-provider-id">{selected.resend_id || "—"}</span></Detail>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div className="border-b border-neutral-900 pb-2">
      <div className="text-[9px] tracking-widest text-neutral-600">{label.toUpperCase()}</div>
      <div className="mt-0.5 text-neutral-200 text-xs break-all">{children}</div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, good, bad }) {
  return (
    <div className={`border bg-appcard p-3.5 ${bad ? "border-red-800" : good ? "border-emerald-800/60" : "border-neutral-800"}`}>
      <div className="flex items-center gap-1.5 text-[9px] tracking-widest text-neutral-500"><Icon size={11} /> {label.toUpperCase()}</div>
      <div className={`heading text-2xl mt-1 ${bad ? "text-red-400" : good ? "text-emerald-400" : ""}`}>{value}</div>
    </div>
  );
}

function GroupBadge({ g, status }) {
  const cfg = {
    delivered: ["DELIVERED", "border-emerald-500/60 text-emerald-400"],
    in_transit: ["IN TRANSIT", "border-neutral-600 text-neutral-400"],
    bounced: ["BOUNCED", "border-red-600 text-red-500"],
    failed: ["FAILED", "border-red-600 text-red-500"],
  }[g] || ["—", "border-neutral-700 text-neutral-500"];
  const label = status === "opened" ? "OPENED" : status === "clicked" ? "CLICKED" : cfg[0];
  return <span className={`text-[9px] tracking-widest px-1.5 py-0.5 border whitespace-nowrap ${cfg[1]}`}>{label}</span>;
}
